import React, { useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from 'lucide-react';

/**
 * Reusable Date Range Filter with presets.
 * Calls onChange({ from: Date|null, to: Date|null, label: string }) whenever selection changes.
 * `value` prop is { from, to, presetKey } so parent controls state.
 */

const PRESETS = [
  { key: 'all', label: 'All Time' },
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'this_quarter', label: 'This Quarter (3 months)' },
  { key: 'half_yearly', label: 'Last 6 Months' },
  { key: 'this_fy', label: 'This Financial Year (Apr-Mar)' },
  { key: 'last_fy', label: 'Last Financial Year' },
  { key: 'this_year', label: 'This Calendar Year' },
  { key: 'custom', label: 'Custom Range' },
];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };

const computeRange = (key, customFrom, customTo) => {
  const now = new Date();
  const today = startOfDay(now);
  const todayEnd = endOfDay(now);

  switch (key) {
    case 'all':
      return { from: null, to: null, label: 'All Time' };
    case 'today':
      return { from: today, to: todayEnd, label: today.toLocaleDateString('en-IN') };
    case 'yesterday': {
      const y = new Date(today); y.setDate(y.getDate() - 1);
      return { from: startOfDay(y), to: endOfDay(y), label: `Yesterday (${y.toLocaleDateString('en-IN')})` };
    }
    case 'this_week': {
      const d = new Date(today);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day + 1); // Monday start
      return { from: d, to: todayEnd, label: `This Week (${d.toLocaleDateString('en-IN')} - ${now.toLocaleDateString('en-IN')})` };
    }
    case 'this_month': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: start, to: todayEnd, label: `${start.toLocaleString('en-IN', { month: 'long', year: 'numeric' })}` };
    }
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: startOfDay(start), to: endOfDay(end), label: start.toLocaleString('en-IN', { month: 'long', year: 'numeric' }) };
    }
    case 'this_quarter': {
      const start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      return { from: startOfDay(start), to: todayEnd, label: `Last 3 Months (${start.toLocaleDateString('en-IN')} - ${now.toLocaleDateString('en-IN')})` };
    }
    case 'half_yearly': {
      const start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
      return { from: startOfDay(start), to: todayEnd, label: `Last 6 Months (${start.toLocaleDateString('en-IN')} - ${now.toLocaleDateString('en-IN')})` };
    }
    case 'this_fy': {
      const y = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
      const start = new Date(y, 3, 1);
      const end = new Date(y + 1, 2, 31);
      return { from: startOfDay(start), to: endOfDay(end), label: `FY ${y}-${String(y + 1).slice(-2)}` };
    }
    case 'last_fy': {
      const y = (now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear()) - 1;
      const start = new Date(y, 3, 1);
      const end = new Date(y + 1, 2, 31);
      return { from: startOfDay(start), to: endOfDay(end), label: `FY ${y}-${String(y + 1).slice(-2)}` };
    }
    case 'this_year': {
      const start = new Date(now.getFullYear(), 0, 1);
      return { from: startOfDay(start), to: todayEnd, label: `${now.getFullYear()}` };
    }
    case 'custom': {
      if (!customFrom || !customTo) return { from: null, to: null, label: 'Custom (pick dates)' };
      return {
        from: startOfDay(new Date(customFrom)),
        to: endOfDay(new Date(customTo)),
        label: `${new Date(customFrom).toLocaleDateString('en-IN')} - ${new Date(customTo).toLocaleDateString('en-IN')}`,
      };
    }
    default:
      return { from: null, to: null, label: 'All Time' };
  }
};

const DateRangeFilter = ({ value, onChange, className = '' }) => {
  const [presetKey, setPresetKey] = useState(value?.presetKey || 'all');
  const [customFrom, setCustomFrom] = useState(value?.customFrom || '');
  const [customTo, setCustomTo] = useState(value?.customTo || '');

  const apply = (key, cf = customFrom, ct = customTo) => {
    const r = computeRange(key, cf, ct);
    onChange?.({ ...r, presetKey: key, customFrom: cf, customTo: ct });
  };

  const handlePresetChange = (key) => {
    setPresetKey(key);
    if (key !== 'custom') apply(key);
  };

  const handleCustomApply = () => {
    if (!customFrom || !customTo) return;
    apply('custom', customFrom, customTo);
  };

  const currentLabel = useMemo(() => {
    if (presetKey === 'custom' && (!customFrom || !customTo)) return 'Pick custom dates';
    return computeRange(presetKey, customFrom, customTo).label;
  }, [presetKey, customFrom, customTo]);

  return (
    <div className={`flex flex-wrap items-end gap-2 ${className}`} data-testid="date-range-filter">
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1">
          <Calendar className="w-3 h-3" /> Date Range
        </span>
        <Select value={presetKey} onValueChange={handlePresetChange}>
          <SelectTrigger className="w-56 h-9" data-testid="date-range-preset">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRESETS.map(p => (
              <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {presetKey === 'custom' && (
        <>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">From</span>
            <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="h-9 w-40" data-testid="date-range-from" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">To</span>
            <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="h-9 w-40" data-testid="date-range-to" />
          </div>
          <Button size="sm" onClick={handleCustomApply} className="h-9 bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="date-range-apply">
            Apply
          </Button>
        </>
      )}

      <span className="text-xs px-2.5 py-1.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 font-medium" data-testid="date-range-label">
        {currentLabel}
      </span>
    </div>
  );
};

/** Helper: filter an array by a date-field within a {from, to} range. */
export const filterByDateRange = (items, from, to, dateField = 'date') => {
  if (!from && !to) return items;
  return items.filter(item => {
    const raw = item?.[dateField];
    if (!raw) return false;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return false;
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });
};

export default DateRangeFilter;
